<template>
  <section>
    <div class="gp-comments__list">
      <div class="gp-comments__list__inner">
        <rui-errors v-if="loadError" :errors="loadError" />
        <rui-loading v-else-if="loading" :text="$t('generic.loading')" />
        <rui-empty v-else-if="comments.length === 0" inline>
          <template v-if="signedIn">
            {{ $t('js.comments.no_comments') }}
          </template>
          <template v-else>
            {{ $t('js.comments.no_comments_anonymous') }}
          </template>
        </rui-empty>

        <gp-comment-entry v-for="comment in comments"
                          :key="comment.id"
                          :comment="comment"
                          @updated="handleUpdatedComment"
                          @destroyed="handleDestroyedComment" />
      </div>
      <div v-if="loadedOnce && signedIn" class="-mtd">
        <h4 class="-mbd -h6">{{ $t('js.comments.new_comment') }}</h4>
        <gp-comment-form :comment="editedComment" class="-m0" @saved="handleNewComment" />
      </div>
    </div>
  </section>
</template>
<script>
import RuiEmpty from '@experiments-labs/rise_ui/src/components/DataDisplay/Empty/Empty.vue'
import RuiErrors from '@experiments-labs/rise_ui/src/components/DataDisplay/Error/Errors.vue'
import RuiLoading from '@experiments-labs/rise_ui/src/components/Loading/Loading.vue'

import Comment from '../../../classes/models/Comment'
import Observation from '../../../classes/models/Observation'
import Task from '../../../classes/models/Task'

import { removeObject, updateOrReplaceObject } from '../../../../helpers/Arrays'

import GpCommentEntry from './_Entry.vue'
import GpCommentForm from './Form.vue'

/**
 * Displays list of comments for the given observation, with the form to create new comments.
 *
 * Loading the list is not automatic. Either:
 * - use the `autoLoad` prop
 * - call the _provided_ `loadComments` method from the parent, on a component _ref_, when loading makes sense
 *
 * @noPlayground
 */
export default {
  name: 'GpCommentsList',
  components: { GpCommentEntry, RuiEmpty, RuiLoading, RuiErrors, GpCommentForm },
  provide: ['loadComments'],
  props: {
    /** Target observation identifier */
    subject: { required: true, type: [Observation, Task] },
    /** Whether to load comments when component is created */
    autoLoad: { required: false, type: Boolean, default: false },
  },
  data () {
    return {
      editedComment: { subject_id: this.$props.subject.id, subject_type: this.$props.subject.className },
      comments: [],
      loadError: null,
      loading: false,
      loadedOnce: false,
    }
  },
  computed: {
    signedIn () { return this.$store.getters.signedIn },
  },
  methods: {
    handleUpdatedComment (comment) { updateOrReplaceObject(this.comments, comment) },
    handleDestroyedComment (comment) { removeObject(this.comments, comment) },
    handleNewComment (comment) {
      this.comments.push(comment)
    },
    loadComments (once = false) {
      if (this.loadedOnce && once) return

      this.loading = true
      this.loadError = null
      Comment.getIndex({ subject: this.$props.subject })
        .then((comments) => {
          this.comments = comments
          this.loadedOnce = true
        })
        .catch((error) => { this.loadError = error })
        .then(() => { this.loading = false })
    },
  },
  created () {
    if (this.$props.autoLoad) this.loadComments(true)
  },
  watch: {
    autoLoad (newValue) {
      if (newValue && !this.loadedOnce) this.loadComments(true)
    },
  },
}
</script>
